<template>
  <div class="card">
    <h3 class="card-title">{{ $t('grid.title') }}</h3>
    
    <div class="grid-settings">
      <div class="setting-group">
        <label for="gridWidth">{{ $t('grid.width') }}</label>
        <input
          id="gridWidth"
          type="number"
          v-model.number="localWidth"
          min="1"
          max="500"
          @change="updateGridSize"
        />
      </div>
      
      <div class="setting-group">
        <label for="gridHeight">{{ $t('grid.height') }}</label>
        <input
          id="gridHeight"
          type="number"
          v-model.number="localHeight"
          min="1"
          max="500"
          @change="updateGridSize"
        />
      </div>
      
      <div class="setting-group">
        <label for="pixelSize">{{ $t('grid.pixelSize') }}</label>
        <select id="pixelSize" v-model.number="previewPixelSize" @change="onPixelSizeChange">
          <option value="2">2px</option>
          <option value="4">4px</option>
          <option value="8">8px</option>
          <option value="16">16px</option>
        </select>
      </div>
      
      <div class="grid-info">
        <p>{{ $t('grid.currentGrid') }} <strong>{{ localWidth }} × {{ localHeight }}</strong></p>
        <p>{{ $t('grid.totalPixels') }} <strong>{{ localWidth * localHeight }}</strong></p>
      </div>

      <div class="help-info">
        <p><strong>{{ $t('grid.help.title') }}</strong></p>
        <ul>
          <li>{{ $t('grid.help.gridSize') }}</li>
          <li>{{ $t('grid.help.pixelSize') }}</li>
          <li>{{ $t('grid.help.tips') }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useColorStore } from '../stores/colorStore'
import { generatePreviewImage } from '../utils/colorUtils'

const colorStore = useColorStore()

const localWidth = ref(colorStore.gridSize.width)
const localHeight = ref(colorStore.gridSize.height)
const previewPixelSize = computed({
  get: () => colorStore.previewPixelSize,
  set: (v: number) => colorStore.setPreviewPixelSize(v)
})

// 监听store中的网格大小变化
watch(() => colorStore.gridSize, (newSize) => {
  localWidth.value = newSize.width
  localHeight.value = newSize.height
}) // 移除deep，因为gridSize只是简单的{width, height}对象

function updateGridSize() {
  if (localWidth.value > 0 && localHeight.value > 0) {
    colorStore.setGridSize(localWidth.value, localHeight.value)
  }
}

async function onPixelSizeChange() {
  if (colorStore.extractedColors) {
    const preview = await generatePreviewImage(
      colorStore.extractedColors.pixels,
      colorStore.previewPixelSize
    )
    colorStore.setPreviewImage(preview)
  }
}
</script>

<style scoped>
.grid-settings {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.setting-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.setting-group label {
  font-weight: 500;
  color: #495057;
}

.setting-group input,
.setting-group select {
  max-width: 200px;
}

.grid-info {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid #667eea;
}

.grid-info p {
  margin: 0.25rem 0;
  color: #495057;
}

.grid-info strong {
  color: #667eea;
}

.help-info {
  background: #fff;
  border: 1px dashed #ced4da;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  color: #495057;
}
.help-info p {
  margin: 0 0 0.5rem 0;
}
.help-info ul {
  margin: 0;
  padding-left: 1.1rem;
  line-height: 1.6;
}
</style>
